// Import 3rd-party dependencies
import { Component, Renderer2, AfterContentInit, OnDestroy, OnInit } from '@angular/core';
import { fromEvent, of, timer } from 'rxjs';
import { catchError, debounceTime, map, shareReplay, switchMap, delay } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as pbi from 'powerbi-client';
import { TeamsMsal2Provider } from '@microsoft/mgt';

// Import project-specific dependencies
import { environment } from '@app/environments/environment';
import { InsightsService } from '@app/core/analytics/services/app-insights-service';
import { WORK_PROFILE_COMPONENT_CONSTS } from '@app/config/constants';
import { PeopleService } from '@app/services/people/people.service';
import { UserProfileService } from '@app/services/user-profile/user-profile.service';
import { RegionWorkplaceService, IRegionWorkplace } from '@app/services/region-workplace/region-workplace.service';

interface IRendererStyle {
  element: unknown;
  style: string;
  value: string;
}

@Component({
  selector: 'app-work-profile',
  templateUrl: './work-profile.component.html',
  styleUrls: ['./work-profile.component.css']
})
export class WorkProfileComponent implements AfterContentInit, OnDestroy, OnInit {
  // Class attributes
  private subs: SubSink;
  activeIndex: number;
  activeHeaderIndex: number;
  pbiProfileInterval: NodeJS.Timeout;
  isReportLoaded: boolean;
  startTime: Date;
  endTime: Date;
  idleTime: number;
  initReportLoad: boolean;
  isEMSreport: boolean;
  myRole: boolean;
  myProfile: boolean;
  myRegion: boolean;
  userRegion: string;
  consts = WORK_PROFILE_COMPONENT_CONSTS;
  regionList: IRegionWorkplace[];
  thumbnailList: IRegionWorkplace[];
  disabledRegionButton: boolean;
  isTeamsApp:boolean;
  messageManager: string

  private readonly coreId$ = this.peopleService.currentUser$.pipe(
    map((user) => user?.employeeId || ''),
    shareReplay()
  );

  readonly isManager$ = this.coreId$.pipe(
    // Get the manager indicator from the DD profile
    switchMap((coreId) => this.userProfileService.getDdProfile(coreId)),
    map((ddUser) => (ddUser.IS_MGR_IND === '1' ? true : false)),
    // Get the manager indicator from the Workday profile
    // switchMap((coreId) => this.userProfileService.getWorkdayProfile(coreId)),
    // map((workday) => workday.isManager),
    catchError(() => of(false)),
    shareReplay()
  );

  // Get the user region (if the user belongs to India country then retun the region as INDIA)
  readonly regionNames$ = this.coreId$.pipe(
    switchMap((coreId) => this.userProfileService.getDdProfile(coreId)),
    map((ddUser) => {
      this.userRegion = ddUser.country === 'IND' ? 'INDIA' : ddUser.CORPORATE_REGION === 'Europe' ?  'Europe Middle East Africa' : ddUser.CORPORATE_REGION;
    })
  );

  constructor(
    private renderer: Renderer2,
    private peopleService: PeopleService,
    private trackingService: InsightsService,
    private userProfileService: UserProfileService,
    private regionWorkplaceService: RegionWorkplaceService,
    private _snackBar: MatSnackBar
  ) {
    this.startTime = new Date();
    this.subs = new SubSink();
    this.initReportLoad = false;
    this.myRole = false;
    this.myRegion = false;
    this.myProfile = true;
    this.disabledRegionButton = true;
    this.isTeamsApp = TeamsMsal2Provider.isAvailable;
  }

  ngAfterContentInit(): void {
    this.subs.sink = fromEvent(window, 'resize')
      .pipe(debounceTime(0))
      .subscribe(() => {
        if (this.initReportLoad) {
          !this.myRegion && this.setReportDimensions();
        }
      });

    // Reload the report when the MyHub button clicked
    const activeHubTab = document.getElementById('MyHubButton');
    this.subs.sink = fromEvent(activeHubTab, 'click')
      .pipe(debounceTime(this.consts.EVENT_DEBOUNCETIME), delay(this.consts.HUB_TAB_EVENT_DELAY))
      .subscribe(() => {
        this.initReportLoad = true;
        this.loadActiveReport();
        this.loadActiveRegion();
      });

    // Load the profile view after idle time
    this.subs.sink = fromEvent(window, 'focus')
      .pipe(debounceTime(this.consts.EVENT_DEBOUNCETIME))
      .subscribe(() => {
        this.endTime = new Date();
        this.isReportLoaded = true;
        this.setRefreshTime();
        const idleTime = Math.floor(this.endTime.getTime() - this.startTime.getTime());
        // Reload the PBI report if the browser is inactive more than REPORT_REFRESH_TIME
        if (idleTime > this.consts.REPORT_REFRESH_TIME) {
          this.checkPbiAuth();
          this.loadActiveReport();
        }
      });

    this.subs.sink = fromEvent(window, 'blur')
      .pipe(debounceTime(this.consts.EVENT_DEBOUNCETIME))
      .subscribe(() => {
        this.isReportLoaded = false;
        this.clearRefreshTime();
      });
    this.checkPbiAuth();
    this.loadActiveRegion();
  }

  ngOnInit(): void {
    // Check if the user has a membership of the DXCInsights_EMS-users group in the Azure AD
    this.subs.sink = this.userProfileService.checkEMSmembership(this.consts.EMS_AAD_GROUP_ID).subscribe((data) => {
      this.isEMSreport = data.value.length ? true : false;
      this.activeIndex = this.consts.PROFILE_REPORTS.PROJECTS.index;
      this.activeHeaderIndex = this.consts.PROFILE_REPORTS.MY_PROFILE.index;
      this.messageManager = this.consts.PROFILE_REPORTS.MESSAGE_MANAGER(Boolean(this.isEMSreport))
      this.loadActiveReport();
    });
  }

  ngOnDestroy(): void {
    this.clearRefreshTime();
    this.subs.unsubscribe();
  }

  // Show My Profile
  showMyProfile(value: string): void {
    const reportContainerDiv = document.getElementById('reportContainer');

    switch (value.trim()) {
      case this.consts.PROFILE_REPORTS.MY_ROLE.label:
        this.activeHeaderIndex = this.consts.PROFILE_REPORTS.MY_ROLE.index;
        this.myProfile = false;
        this.myRegion = false;
        this.myRole = true;
        this.renderer.removeClass(reportContainerDiv, 'hidden');
        this.isEMSreport
          ? this.showPowerBIReport(this.consts.PROFILE_REPORTS.ACCOUNT_EXECUTIVE.label)
          : this.showPowerBIReport(this.consts.PROFILE_REPORTS.MY_TEAM.label);
        break;
      case this.consts.PROFILE_REPORTS.MY_REGION.label:
        this.activeHeaderIndex = this.consts.PROFILE_REPORTS.MY_REGION.index;
        this.myProfile = false;
        this.myRegion = true;
        this.myRole = false;
        this.renderer.addClass(reportContainerDiv, 'hidden');
        this.loadActiveRegion();
        break;

      default:
        this.activeHeaderIndex = this.consts.PROFILE_REPORTS.MY_PROFILE.index;
        this.myProfile = true;
        this.myRegion = false;
        this.myRole = false;
        this.renderer.removeClass(reportContainerDiv, 'hidden');
        this.showPowerBIReport(this.consts.PROFILE_REPORTS.PROJECTS.label);
        break;
    }
  }
  // Show the Power BI embed report
  showPowerBIReport(value: string): void {
    this.checkPbiAuth();
    this.startTime = new Date();
    const reportContainer = document.getElementById('reportContainer');
    const activeReportId =
      this.consts.PROFILE_REPORTS.MY_TEAM.label === value.trim()
        ? environment.POWERBI.REPORT_ID_MYTEAM
        : environment.POWERBI.REPORT_ID_PROFILE;

    if (value.trim() !== this.consts.PROFILE_REPORTS.ACCOUNT_EXECUTIVE.label) {
      const activeReportURL = `${environment.POWERBI.REPORT_BASE_URL}${activeReportId}`;
      const config = {
        type: this.consts.CONFIG_TYPE,
        accessToken: ' ',
        permissions: pbi.models.Permissions.All,
        embedUrl: activeReportURL,
        tokenType: pbi.models.TokenType.Aad,
        id: activeReportId,
        settings: {
          filterPaneEnabled: false,
          navContentPaneEnabled: false
        }
      };
      // Host the report.
      const powerbi = new pbi.service.Service(
        pbi.factories.hpmFactory,
        pbi.factories.wpmpFactory,
        pbi.factories.routerFactory
      );
      powerbi.reset(reportContainer);
      powerbi.embed(reportContainer, config);
    }

    const location = this.consts.APP_INSIGHTS_LOCATION;
    let clickEventName = '';
    let reportEmbedUrl = `${environment.POWERBI.EMBED_URL}${activeReportId}`;
    let iframeClass = 'report-powerbi';
    switch (value.trim()) {
      case this.consts.PROFILE_REPORTS.TIMETRACKING.label:
        reportEmbedUrl = `${reportEmbedUrl}${this.consts.PROFILE_REPORTS.TIMETRACKING.url}${environment.POWERBI.REPORT_CONFIG}`;
        clickEventName = this.consts.PROFILE_REPORTS.TIMETRACKING.label;
        this.activeIndex = this.consts.PROFILE_REPORTS.TIMETRACKING.index;
        break;
      case this.consts.PROFILE_REPORTS.SKILLS.label:
        reportEmbedUrl = `${reportEmbedUrl}${this.consts.PROFILE_REPORTS.SKILLS.url}${environment.POWERBI.REPORT_CONFIG}`;
        clickEventName = this.consts.PROFILE_REPORTS.SKILLS.label;
        this.activeIndex = this.consts.PROFILE_REPORTS.SKILLS.index;
        break;
      case this.consts.PROFILE_REPORTS.MY_TEAM.label:
        reportEmbedUrl = `${reportEmbedUrl}${environment.POWERBI.REPORT_CONFIG}`;
        clickEventName = this.consts.PROFILE_REPORTS.MY_TEAM.label;
        this.activeIndex = this.consts.PROFILE_REPORTS.MY_TEAM.index;
        break;
      case this.consts.PROFILE_REPORTS.ACCOUNT_EXECUTIVE.label:
        reportEmbedUrl = this.consts.PROFILE_REPORTS.ACCOUNT_EXECUTIVE.url;
        clickEventName = this.consts.PROFILE_REPORTS.ACCOUNT_EXECUTIVE.label;
        this.activeIndex = this.consts.PROFILE_REPORTS.ACCOUNT_EXECUTIVE.index;
        iframeClass = 'report-powerapps';
        break;
      default:
        reportEmbedUrl = `${reportEmbedUrl}${this.consts.PROFILE_REPORTS.PROJECTS.url}${environment.POWERBI.REPORT_CONFIG}`;
        clickEventName = this.consts.PROFILE_REPORTS.PROJECTS.label;
        this.activeIndex = this.consts.PROFILE_REPORTS.PROJECTS.index;
        break;
    }

    this.trackingService.trackClickEvent({
      name: clickEventName,
      location: location,
      title: clickEventName
    });

    this.renderer.setProperty(
      reportContainer,
      'innerHTML',
      `<iframe id='reportIframe' class='${iframeClass}' src='${reportEmbedUrl}' width='100%' height='100%' frameborder='0'></iframe>`
    );
    // Check whether the iframe loaded and calc loading time
    const reportIframeElement = document.getElementById('reportIframe');
    this.renderer.listen(reportIframeElement, 'load', () => {
      this.isReportLoaded = true;
      !this.myRegion && this.setReportDimensions();
    });
  }

  // Set the dimensions of the PBI report based on the page layout
  setReportDimensions(): void {
    const activeTab = document.querySelector('.report-button.active').innerHTML.trim();
    const profileElement = document.getElementById('HubWorkProfile');
    const reportContainerElement = document.getElementById('reportContainer');
    const reportIframeElement = document.getElementById('reportIframe');
    const reportWidth = profileElement.clientWidth;
    let reportContainerMarginX = this.consts.PROFILE_REPORT_CONTAINER_MARGIN_X;
    let reportContainerMarginY = this.consts.PROFILE_REPORT_CONTAINER_MARGIN_Y;
    let reportHeight = 0;
    switch (activeTab) {
      case this.consts.PROFILE_REPORTS.MY_TEAM.label:
        reportHeight = reportWidth / this.consts.MYTEAM_REPORT_SCALE_RATIO;
        break;
      case this.consts.PROFILE_REPORTS.ACCOUNT_EXECUTIVE.label:
        reportHeight = reportWidth / this.consts.ACCOUNT_EXECUTIVES_REPORT_SCALE_RATIO;
        reportContainerMarginX = this.consts.ACCOUNT_EXECUTIVES_REPORT_CONTAINER_MARGIN_X;
        reportContainerMarginY = this.consts.ACCOUNT_EXECUTIVES_REPORT_CONTAINER_MARGIN_Y;
        break;
      default:
        reportHeight = reportWidth / this.consts.PROFILE_REPORT_SCALE_RATIO;
        break;
    }
    const styles: IRendererStyle[] = [
      {
        element: reportContainerElement,
        style: 'height',
        value: `${reportHeight - reportContainerMarginY}px`
      },
      {
        element: reportContainerElement,
        style: 'width',
        value: `${reportWidth - reportContainerMarginX}px`
      },
      {
        element: reportIframeElement,
        style: 'height',
        value: `${reportHeight}px`
      },
      {
        element: reportIframeElement,
        style: 'width',
        value: `${reportWidth}px`
      }
    ];

    styles.forEach((style: IRendererStyle) => {
      this.renderer.setStyle(style.element, style.style, style.value);
    });
  }

  // Set the interval to refresh the report in the time specified in the REPORT_REFRESH_TIME constant
  setRefreshTime(): void {
    this.clearRefreshTime();
    this.pbiProfileInterval = setInterval(() => {
      this.loadActiveReport();
    }, this.consts.REPORT_REFRESH_TIME);
  }

  // Clear interval
  clearRefreshTime(): void {
    if (this.pbiProfileInterval) {
      clearInterval(this.pbiProfileInterval);
    }
  }

  // Load the PBI report auto auth URL into the hidden div
  checkPbiAuth(): void {
    const authContainer = document.getElementById('authContainer');
    this.renderer.setProperty(
      authContainer,
      'innerHTML',
      `<iframe src='${environment.POWERBI.REPORT_AUTO_AUTH_LOGIN}' width='0' height='0' frameborder='0'></iframe>`
    );
  }

  // Load the active PBI report
  loadActiveReport(): void {
    this.subs.sink = timer(this.consts.ACTIVE_REPORT_TIMEOUT).subscribe(() => {
      const activeReportTab = document.querySelector('.report-button.active').innerHTML.trim();
      this.showPowerBIReport(activeReportTab);
    });
  }

  // Get the region based and workplace based data from Json
  loadActiveRegion(): void {
    this.subs.sink = this.regionWorkplaceService.regionWorkplace$
    .pipe(debounceTime(this.consts.EVENT_DEBOUNCETIME), delay(this.consts.ACTIVE_REGION_REPORT_DELAY))
    .subscribe((res) => {
      if (res) {
        this.regionList = res.filter((obj) => {
          return obj.region === this.userRegion;
        });

        this.thumbnailList = res.filter((obj) => {
          return (
            obj.region === this.userRegion &&
            (obj.thumbnailpath.indexOf(this.consts.WORKPLACE_THUMBNAIL_IMAGE_URL) != -1 ||
              obj.thumbnailpath.indexOf(this.consts.REGION_THUMBNAIL_IMAGE_URL) != -1)
          );
        });
        const filterRegionImagePath = this.regionList.filter((item) => (item.title.endsWith('Region'))).map((item) => item);
        const filterWorkplaceImagePath = this.regionList.filter((item) => (item.title.endsWith('Workplace'))).map((item) => item);
        this.regionList = filterRegionImagePath.length !== 0 ? filterRegionImagePath : filterWorkplaceImagePath;
        this.disabledRegionButton = this.regionList.length === 0 ? true : false;
      }
    });
  }

  // open link in new tab
  openLink(url: string): void {
    window.open(url, '_blank');
  }

  // copy link
  openSnackBar(): void {
    this._snackBar.open(this.consts.MESSAGE_COPY_LINK, undefined, {
      duration: this.consts.SNACKBAR_DURATION
    });
  }

  // open PBI report page in the browser window
  redirectPBIreportURL(): void {
    const env = environment.production ? environment.APP.URL.PUBLIC : window.location.origin;
    window.open(`${env}/#WorkProfile`, '_blank');
  }
}
